import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Pagination from '../components/common/Pagination';
import api from '../api/axiosConfig';
import ImprovedClaimModal from '../components/ImprovedClaimModal';
const CustomerConsultation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isReception = location.pathname.includes('/reception');
    const isProcessing = location.pathname.includes('/processing');
    const [consultations, setConsultations] = useState([]);
    const [filteredConsultations, setFilteredConsultations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('recipient_name');
    const [selectedConsultations, setSelectedConsultations] = useState([]);
    const [status, setStatus] = useState('');
    const [showDetailSearch, setShowDetailSearch] = useState(false);
    // 최근 1주일 날짜 계산
    const getInitialDates = () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(end.getMonth() - 3); // 3개월로 변경
        return {
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        };
    };
    const initialDates = getInitialDates();
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [dateColumn, setDateColumn] = useState('created_at');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [ordersTotalCount, setOrdersTotalCount] = useState(0);
    const [consultationsLoaded, setConsultationsLoaded] = useState(false);
    // 파일 업로드를 위한 input ref 추가
    const fileInputRef = useRef(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadedConsultations, setUploadedConsultations] = useState([]);
    const [uploadError, setUploadError] = useState('');
    const [orders, setOrders] = useState([]);
    // 고객 상담 데이터 로드
    const loadConsultations = async () => {
        setLoading(true);
        try {
            // 기간 필터 적용하여 요청
            const response = await api.get(`/api/customer-consultations`, {
                params: {
                    startDate,
                    endDate
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                // mall_name을 mall_id로 매핑하여 처리
                const mappedData = response.data.data.map((consultation, index) => {
                    // 데이터 유효성 검사
                    if (!consultation || typeof consultation !== 'object') {
                        console.warn(`Index ${index}: 유효하지 않은 상담 데이터`, consultation);
                        return null;
                    }
                    // 쇼핑몰 ID 생성 로직
                    let mallId = consultation.mall_name || '';
                    // mall_name이 비어있는 경우 임시 쇼핑몰 ID 생성
                    if (!mallId || mallId.trim() === '') {
                        const testMallIds = ['쇼핑롱', '오케이몰', '네이버쇼핑', '쿠팡', '11번가', 'G마켓', '옥션'];
                        mallId = testMallIds[index % testMallIds.length];
                    }
                    return {
                        ...consultation,
                        mall_id: mallId // 생성된 또는 기존 mall_name을 mall_id로 매핑
                    };
                }).filter((item) => item !== null); // null 값 제거
                setConsultations(mappedData);
                setConsultationsLoaded(true);
                return mappedData;
            }
            else {
                console.error('응답 데이터 형식이 올바르지 않습니다:', response.data);
                setConsultations([]);
                setConsultationsLoaded(false);
                return [];
            }
        }
        catch (error) {
            console.error('고객 상담 데이터 로드 실패:', error);
            setConsultations([]);
            setConsultationsLoaded(false);
            return [];
        }
        finally {
            setLoading(false);
        }
    };
    // 주문 데이터 로드
    const loadOrders = async () => {
        setLoading(true);
        try {
            const requestedLimit = itemsPerPage === -1 ? -1 : itemsPerPage;
            const response = await api.get(`/api/orders/all`, {
                params: {
                    startDate,
                    endDate,
                    searchTerm: searchTerm.trim() || undefined,
                    searchColumn,
                    page: currentPage,
                    limit: requestedLimit
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.orders)) {
                setOrders(response.data.orders);
                setOrdersTotalCount(Number(response.data.totalCount) || response.data.orders.length || 0);
            }
            else {
                setOrders([]);
                setOrdersTotalCount(0);
            }
        }
        catch (error) {
            console.error('주문 데이터 불러오기 실패:', error);
            setOrders([]);
            setOrdersTotalCount(0);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadOrders();
    }, [currentPage, itemsPerPage]);
    // 검색 결과 필터링
    useEffect(() => {
        let filtered = [...consultations];
        if (searchTerm) {
            filtered = filtered.filter(consultation => {
                const value = consultation[searchColumn];
                return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        if (status) {
            filtered = filtered.filter(consultation => consultation.status === status);
        }
        if (startDate && endDate) {
            filtered = filtered.filter(consultation => {
                const date = consultation[dateColumn];
                const consultationDate = new Date(date).toISOString().split('T')[0];
                return consultationDate >= startDate && consultationDate <= endDate;
            });
        }
        setFilteredConsultations(filtered);
    }, [consultations, searchTerm, searchColumn, status, startDate, endDate, dateColumn]);
    // 상담 선택 처리
    const handleSelectConsultation = (e, id) => {
        if (e.target.checked) {
            setSelectedConsultations([...selectedConsultations, id]);
        }
        else {
            setSelectedConsultations(selectedConsultations.filter(consultationId => consultationId !== id));
        }
    };
    // 전체 선택/해제
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedConsultations(filteredConsultations.map(consultation => consultation.id));
        }
        else {
            setSelectedConsultations([]);
        }
    };
    // 상담 삭제
    const handleDeleteConsultation = async (id) => {
        if (window.confirm('선택한 상담내역을 삭제하시겠습니까?')) {
            try {
                await api.delete(`/api/customer-consultations/${id}`);
                await loadConsultations(); // 데이터 다시 로드
            }
            catch (error) {
                console.error('상담 삭제 실패:', error);
                alert('상담 삭제에 실패했습니다.');
            }
        }
    };
    // 선택된 상담 삭제
    const handleDeleteSelected = async () => {
        if (selectedConsultations.length === 0) {
            alert('삭제할 상담을 선택해주세요.');
            return;
        }
        if (window.confirm(`선택한 ${selectedConsultations.length}개의 상담내역을 삭제하시겠습니까?`)) {
            try {
                await Promise.all(selectedConsultations.map(id => api.delete(`/api/customer-consultations/${id}`)));
                await loadConsultations(); // 데이터 다시 로드
                setSelectedConsultations([]);
            }
            catch (error) {
                console.error('상담 삭제 실패:', error);
                alert('상담 삭제에 실패했습니다.');
            }
        }
    };
    // 검색어 변경 처리
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };
    // 검색 실행 처리
    const handleSearch = () => {
        setConsultationsLoaded(false);
        if (currentPage !== 1) {
            setCurrentPage(1);
            return;
        }
        loadOrders();
    };
    // 키 입력 이벤트 처리
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    // 검색 컬럼 변경 처리
    const handleSearchColumnChange = (e) => {
        setSearchColumn(e.target.value);
    };
    // 상태 변경 처리
    const handleStatusChange = (e) => {
        setStatus(e.target.value);
    };
    // 상세 검색 토글
    const toggleDetailSearch = () => {
        setShowDetailSearch(!showDetailSearch);
    };
    // 필터 초기화
    const clearFilters = () => {
        setSearchTerm('');
        setSearchColumn('recipient_name');
        setStatus('');
        setStartDate('');
        setEndDate('');
        setDateColumn('created_at');
        setNewProcess({
            id: 0,
            order_number: '',
            order_date: '',
            mall_id: '',
            product_name: '',
            recipient_name: '',
            contact: '',
            address: '',
            process_type: '',
            receiver: '',
            claim_content: '',
            bank_name: '',
            account_number: '',
            depositor: '',
            refund_amount: 0,
            // 배송 정보
            courier_company: '',
            courier_fee: 0,
            return_box: 0,
            return_fee_type: '고객부담',
            // 반품 정보
            send_courier_fee: 0,
            send_box: 0,
            send_fee_type: '고객부담',
            send_courier_company: '',
            status: '접수',
            created_at: '',
            updated_at: '',
            created_by: '',
            updated_by: ''
        });
    };
    // 엑셀로 다운로드하는 함수
    const handleDownloadExcel = async () => {
        const sourceConsultations = consultationsLoaded
            ? consultations
            : await loadConsultations();
        // 사용자가 선택한 항목들만 필터링
        const consultationsToDownload = selectedConsultations.length > 0
            ? sourceConsultations.filter((consultation) => selectedConsultations.includes(consultation.id))
            : sourceConsultations; // 선택된 항목이 없으면 전체 항목 다운로드
        // 엑셀에 표시할 항목 추출
        const worksheet = XLSX.utils.json_to_sheet(consultationsToDownload.map(consultation => ({
            '상담일': consultation.created_at,
            '오더번호': consultation.order_number,
            '쇼핑몰명': consultation.mall_id,
            '상품명': consultation.product_name,
            '수취자': consultation.recipient_name,
            '연락처': consultation.contact,
            '주소': consultation.address,
            '처리유형': consultation.process_type,
            '접수내용': consultation.claim_content,
            '은행명': consultation.bank_name,
            '계좌번호': consultation.account_number,
            '입금자명': consultation.depositor,
            '금액': consultation.refund_amount,
            '처리상태': consultation.status
        })));
        // 엑셀 파일 생성
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '상담내역');
        XLSX.writeFile(workbook, selectedConsultations.length > 0 ? '선택한_상담내역.xlsx' : '전체_상담내역.xlsx');
    };
    // 템플릿 다운로드
    const handleDownloadTemplate = () => {
        const template = [
            {
                '상담일': '',
                '오더번호': '',
                '쇼핑몰명': '',
                '상품명': '',
                '수취자': '',
                '연락처': '',
                '주소': '',
                '처리유형': '',
                '접수내용': '',
                '은행명': '',
                '계좌번호': '',
                '입금자명': '',
                '금액': '',
                '처리상태': ''
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(template);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '상담접수_템플릿');
        XLSX.writeFile(workbook, '상담접수_템플릿.xlsx');
    };
    // 파일 업로드 모달 토글
    const toggleUploadModal = () => {
        setShowUploadModal(!showUploadModal);
        if (!showUploadModal) {
            setUploadedConsultations([]);
            setUploadError('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    // 파일 업로드 처리
    const handleFileUpload = (e) => {
        var _a;
        const file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (e) => {
            var _a;
            try {
                const data = (_a = e.target) === null || _a === void 0 ? void 0 : _a.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(sheet);
                // 엑셀 데이터를 상담 객체로 변환
                const newConsultations = json.map((row, index) => ({
                    id: index,
                    order_number: row['오더번호'] || '',
                    order_date: row['오더날짜'] || '',
                    mall_id: row['쇼핑몰명'] || '',
                    product_name: row['상품명'] || '',
                    recipient_name: row['수취자'] || '',
                    contact: row['연락처'] || '',
                    address: row['주소'] || '',
                    process_type: row['처리유형'] || '',
                    receiver: row['접수자'] || '',
                    claim_content: row['접수내용'] || '',
                    bank_name: row['은행명'] || '',
                    account_number: row['계좌번호'] || '',
                    depositor: row['입금자명'] || '',
                    refund_amount: Number(row['금액']) || 0,
                    // 배송 정보
                    courier_company: row['배송업체'] || '',
                    courier_fee: Number(row['배송비']) || 0,
                    return_box: Number(row['배송박스']) || 0,
                    return_fee_type: row['배송비부담'] || '고객부담',
                    // 반품 정보
                    send_courier_fee: Number(row['반품배송비']) || 0,
                    send_box: Number(row['반품박스']) || 0,
                    send_fee_type: row['반품비부담'] || '고객부담',
                    send_courier_company: row['반품배송업체'] || '',
                    status: row['처리상태'] || '접수',
                    created_at: new Date().toISOString().split('T')[0],
                    updated_at: new Date().toISOString().split('T')[0],
                    created_by: '',
                    updated_by: ''
                }));
                setUploadedConsultations(newConsultations);
                setUploadError('');
            }
            catch (error) {
                console.error('Excel file parsing error:', error);
                setUploadError('엑셀 파일 처리 중 오류가 발생했습니다.');
                setUploadedConsultations([]);
            }
        };
        reader.onerror = () => {
            setUploadError('파일을 읽는 중 오류가 발생했습니다.');
            setUploadedConsultations([]);
        };
        reader.readAsBinaryString(file);
    };
    // 업로드된 데이터 저장
    const saveUploadedConsultations = () => {
        if (uploadedConsultations.length === 0) {
            alert('등록할 데이터가 없습니다.');
            return;
        }
        // 고유 ID 부여
        const newConsultations = uploadedConsultations.map((consultation, index) => ({
            ...consultation,
            id: index
        }));
        setConsultations([...consultations, ...newConsultations]);
        setShowUploadModal(false);
        setUploadedConsultations([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        alert(`${newConsultations.length}개의 상담내역이 등록되었습니다.`);
    };
    // 페이지네이션 계산
    const indexOfLastItem = itemsPerPage === -1 ? filteredConsultations.length : currentPage * itemsPerPage;
    const indexOfFirstItem = itemsPerPage === -1 ? 0 : indexOfLastItem - itemsPerPage;
    const currentItems = filteredConsultations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = itemsPerPage === -1 ? 1 : Math.max(1, Math.ceil(filteredConsultations.length / itemsPerPage));
    // 페이지 변경 핸들러
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    // 페이지당 항목 수 변경 핸들러
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    // 페이지 제목 설정
    const pageTitle = isReception ? '상담접수 관리' : isProcessing ? '상담처리 관리' : '고객 상담 관리';
    // 신규 상담 등록 관련 상태 및 핸들러
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [newProcess, setNewProcess] = useState({
        id: 0,
        order_number: '',
        order_date: '',
        mall_id: '',
        product_name: '',
        recipient_name: '',
        contact: '',
        address: '',
        process_type: '',
        receiver: '',
        claim_content: '',
        bank_name: '',
        account_number: '',
        depositor: '',
        refund_amount: 0,
        // 배송 정보
        courier_company: '',
        courier_fee: 0,
        return_box: 0,
        return_fee_type: '고객부담',
        // 반품 정보
        send_courier_fee: 0,
        send_box: 0,
        send_fee_type: '고객부담',
        send_courier_company: '',
        status: '접수',
        created_at: '',
        updated_at: '',
        created_by: '',
        updated_by: ''
    });
    const handleNewProcessChange = (e) => {
        const { id, value } = e.target;
        setNewProcess(prev => ({ ...prev, [id]: value }));
    };
    const handleAddProcess = () => {
        setConsultations([...consultations, newProcess]);
        setShowProcessModal(false);
        setNewProcess({
            id: 0,
            order_number: '',
            order_date: '',
            mall_id: '',
            product_name: '',
            recipient_name: '',
            contact: '',
            address: '',
            process_type: '',
            receiver: '',
            claim_content: '',
            bank_name: '',
            account_number: '',
            depositor: '',
            refund_amount: 0,
            // 배송 정보
            courier_company: '',
            courier_fee: 0,
            return_box: 0,
            return_fee_type: '고객부담',
            // 반품 정보
            send_courier_fee: 0,
            send_box: 0,
            send_fee_type: '고객부담',
            send_courier_company: '',
            status: '접수',
            created_at: '',
            updated_at: '',
            created_by: '',
            updated_by: ''
        });
    };
    const toggleProcessModal = () => {
        setShowProcessModal(!showProcessModal);
    };
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimOrder, setClaimOrder] = useState(null);
    const [isClaimLoading, setIsClaimLoading] = useState(false);
    // 클레임 모달 열기
    const openClaimModal = (order) => {
        // 주문 데이터를 클레임 모달에 맞는 형식으로 변환
        const orderData = {
            orderNumber: order.order_number,
            channel_order_id: order.channel_order_id,
            orderDate: order.order_date,
            mallName: order.mall_id,
            customerName: order.customer_name,
            recipientName: order.recipient_name,
            contact: order.mobile_phone,
            address: order.address,
            zipCode: order.zip_code,
            products: [{
                    productCode: order.product_code || '',
                    productName: order.product_name,
                    productType: '일반상품',
                    productOption: order.product_option || '',
                    originalQuantity: order.quantity || 1,
                    claimQuantity: 1,
                    unitPrice: order.unit_price || 0,
                    totalPrice: order.total_price || 0,
                    claimAmount: order.unit_price || 0,
                    productClaimReason: '',
                    productStatus: '접수'
                }]
        };
        setClaimOrder(orderData);
        setShowClaimModal(true);
    };
    // 클레임 제출 핸들러
    const handleClaimSubmit = async (claimData) => {
        setIsClaimLoading(true);
        try {
            const requestData = {
                orderNumber: claimData.orderNumber,
                orderDate: claimData.orderDate,
                mallName: claimData.mallName,
                customerName: claimData.customerName,
                recipientName: claimData.recipientName,
                contact: claimData.contact,
                address: claimData.address,
                zipCode: claimData.zipCode,
                processType: claimData.processType,
                claimType: claimData.claimType,
                claimReason: claimData.claimReason,
                claimContent: claimData.claimContent,
                receiver: claimData.receiver,
                priority: claimData.priority,
                refundType: claimData.refundType,
                bankName: claimData.bankName,
                accountNumber: claimData.accountNumber,
                depositor: claimData.depositor,
                refundAmount: claimData.refundAmount,
                claimComp: claimData.claimComp,
                // 배송 정보
                courier_company: claimData.deliveryCourierCompany || '',
                courier_fee: Number(claimData.courierFee) || 0,
                return_box: Number(claimData.deliveryBoxCount) || 0,
                return_fee_type: claimData.deliveryFeeType || '고객부담',
                // 반품 정보
                send_courier_fee: Number(claimData.returnFee) || 0,
                send_box: Number(claimData.returnBoxCount) || 0,
                send_fee_type: claimData.returnFeeType || '고객부담',
                send_courier_company: claimData.returnCourierCompany || '',
                products: claimData.products,
                status: '접수',
                // 생성/수정 정보 추가
                created_at: new Date().toISOString().split('T')[0],
                updated_at: new Date().toISOString().split('T')[0],
                createdBy: 'system',
                updated_by: 'system'
            };
            const response = await api.post(`/api/consultations`, requestData);
            if (response.data.success) {
                alert('클레임이 성공적으로 등록되었습니다.');
                handleClaimClose();
                loadConsultations();
            }
            else {
                alert('클레임 등록에 실패했습니다.');
            }
        }
        catch (error) {
            console.error('클레임 등록 오류:', error);
            alert('클레임 등록 중 오류가 발생했습니다.');
        }
        finally {
            setIsClaimLoading(false);
        }
    };
    // 클레임 모달 닫기
    const handleClaimClose = () => {
        setShowClaimModal(false);
        setClaimOrder(null);
    };
    // 주문 목록은 서버에서 현재 페이지 분량만 조회
    const totalPagesOrders = itemsPerPage === -1 ? 1 : Math.max(1, Math.ceil(ordersTotalCount / itemsPerPage));
    const currentItemsOrders = orders;
    return (_jsxs("div", { className: "flex flex-col h-full -m-3", children: [_jsx("div", { className: "flex-shrink-0 bg-gray-100 p-3", children: _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: pageTitle }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: handleDownloadTemplate, children: "\uD15C\uD50C\uB9BF \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 bg-green-600 text-white text-sm rounded-md hover:bg-green-700", onClick: handleDownloadExcel, children: "\uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: toggleUploadModal, children: "\uC5D1\uC140 \uC5C5\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: toggleProcessModal, children: "\uC0C1\uB2F4\uCC98\uB9AC \uB4F1\uB85D" })] })] }) }), _jsxs("div", { className: "flex-1 overflow-auto px-3 pb-3 bg-gray-100", children: [_jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm p-4 mb-3", children: _jsxs("div", { className: "flex items-center flex-wrap gap-3", children: [_jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: searchColumn, onChange: (e) => setSearchColumn(e.target.value), children: [_jsx("option", { value: "order_number", children: "\uC624\uB354\uBC88\uD638" }), _jsx("option", { value: "recipient_name", children: "\uC218\uCDE8\uC790" }), _jsx("option", { value: "mall_id", children: "\uC1FC\uD551\uBAB0" }), _jsx("option", { value: "product_name", children: "\uC0C1\uD488\uBA85" })] }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-56", placeholder: "\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD558\uC138\uC694", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyPress: handleKeyPress }), _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-bold", type: "button", onClick: handleSearch, children: "\uAC80\uC0C9" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: status, onChange: (e) => setStatus(e.target.value), children: [_jsx("option", { value: "", children: "\uBAA8\uB4E0 \uC0C1\uD0DC" }), _jsx("option", { value: "\uC811\uC218", children: "\uC811\uC218" }), _jsx("option", { value: "\uCC98\uB9AC\uC911", children: "\uCC98\uB9AC\uC911" }), _jsx("option", { value: "\uC644\uB8CC", children: "\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" })] }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: startDate, onChange: (e) => setStartDate(e.target.value) }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: endDate, onChange: (e) => setEndDate(e.target.value) })] }) }), _jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden", children: _jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "min-w-full text-sm", children: [_jsx("thead", { className: "bg-gray-50 sticky top-0 z-10", children: _jsxs("tr", { children: [_jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "No." }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC624\uB354\uB0A0\uC9DC" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC624\uB354\uBC88\uD638" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC1FC\uD551\uBAB0" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC8FC\uBB38\uC790" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC218\uCDE8\uC790" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC5F0\uB77D\uCC98" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC6B0\uD3B8\uBC88\uD638" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC8FC\uC18C" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uC0C1\uD488\uBA85" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uAC2F\uC218" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", children: "\uB3C4\uAD6C\uD568" })] }) }), _jsx("tbody", { children: currentItemsOrders.map((order, idx) => (_jsxs("tr", { className: "border-t border-gray-200 hover:bg-gray-50", children: [_jsx("td", { className: "px-3 py-2 text-sm", children: (currentPage - 1) * itemsPerPage + idx + 1 }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.order_date }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.order_number }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.mall_id }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.customer_name }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.recipient_name }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.mobile_phone }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.zip_code }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.address }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.product_name }), _jsx("td", { className: "px-3 py-2 text-sm", children: order.quantity }), _jsx("td", { className: "px-3 py-2 text-sm", children: _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: () => openClaimModal(order), children: "\uD074\uB808\uC784 \uC811\uC218" }) })] }, order.id || idx))) })] }) }) }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPagesOrders, itemsPerPage: itemsPerPage, totalItems: ordersTotalCount, onPageChange: setCurrentPage, onItemsPerPageChange: setItemsPerPage }), showProcessModal && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col", children: [_jsxs("div", { className: "flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg", children: [_jsx("h5", { className: "text-lg font-bold text-gray-900", children: "\uC0C1\uB2F4\uCC98\uB9AC \uB4F1\uB85D" }), _jsx("button", { type: "button", className: "text-gray-400 hover:text-gray-600 text-xl leading-none", onClick: toggleProcessModal, children: "x" })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: [_jsx("div", { className: "bg-gray-50 px-4 py-2 border-b border-gray-200 rounded-t-lg", children: _jsx("h6", { className: "font-bold text-sm text-gray-900", children: "\uAE30\uBCF8 \uC815\uBCF4" }) }), _jsxs("div", { className: "p-4 grid grid-cols-4 gap-3", children: [_jsxs("div", { children: [_jsxs("label", { htmlFor: "created_at", className: "block text-sm font-medium text-gray-700 mb-1", children: ["\uC0C1\uB2F4\uC77C ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "created_at", name: "created_at", value: newProcess.created_at || '', onChange: handleNewProcessChange, required: true })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "order_date", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC8FC\uBB38\uC77C" }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "order_date", name: "order_date", value: newProcess.order_date || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "courier_company", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uD0DD\uBC30\uC0AC" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "courier_company", name: "courier_company", value: newProcess.courier_company || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "mall_id", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC1FC\uD551\uBAB0\uBA85" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "mall_id", name: "mall_id", value: newProcess.mall_id || '', onChange: handleNewProcessChange })] })] })] }), _jsxs("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: [_jsx("div", { className: "bg-gray-50 px-4 py-2 border-b border-gray-200 rounded-t-lg", children: _jsx("h6", { className: "font-bold text-sm text-gray-900", children: "\uACE0\uAC1D \uC815\uBCF4" }) }), _jsxs("div", { className: "p-4 grid grid-cols-4 gap-3", children: [_jsxs("div", { children: [_jsxs("label", { htmlFor: "recipient_name", className: "block text-sm font-medium text-gray-700 mb-1", children: ["\uC218\uCDE8\uC778 ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "recipient_name", name: "recipient_name", value: newProcess.recipient_name || '', onChange: handleNewProcessChange, required: true })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "contact", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC5F0\uB77D\uCC98" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "contact", name: "contact", value: newProcess.contact || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "receiver", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC811\uC218\uC790" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "receiver", name: "receiver", value: newProcess.receiver || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "order_number", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC624\uB354\uBC88\uD638" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "order_number", name: "order_number", value: newProcess.order_number || '', onChange: handleNewProcessChange })] }), _jsxs("div", { className: "col-span-4", children: [_jsx("label", { htmlFor: "address", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC8FC\uC18C" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "address", name: "address", value: newProcess.address || '', onChange: handleNewProcessChange })] })] })] }), _jsxs("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: [_jsx("div", { className: "bg-gray-50 px-4 py-2 border-b border-gray-200 rounded-t-lg", children: _jsx("h6", { className: "font-bold text-sm text-gray-900", children: "\uC0C1\uD488 \uC815\uBCF4" }) }), _jsxs("div", { className: "p-4 grid grid-cols-2 gap-3", children: [_jsxs("div", { children: [_jsxs("label", { htmlFor: "product_name", className: "block text-sm font-medium text-gray-700 mb-1", children: ["\uC0C1\uD488\uBA85 ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "product_name", name: "product_name", value: newProcess.product_name || '', onChange: handleNewProcessChange, required: true })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "refund_amount", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uD658\uBD88\uAE08\uC561" }), _jsx("input", { type: "number", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "refund_amount", name: "refund_amount", value: newProcess.refund_amount || '', onChange: handleNewProcessChange })] })] })] }), _jsxs("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: [_jsx("div", { className: "bg-gray-50 px-4 py-2 border-b border-gray-200 rounded-t-lg", children: _jsx("h6", { className: "font-bold text-sm text-gray-900", children: "\uCC98\uB9AC \uC815\uBCF4" }) }), _jsxs("div", { className: "p-4 grid grid-cols-4 gap-3", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "process_type", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uCC98\uB9AC\uC720\uD615" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "process_type", name: "process_type", value: newProcess.process_type || '', onChange: handleNewProcessChange, children: [_jsx("option", { value: "", children: "\uC120\uD0DD\uD558\uC138\uC694" }), _jsx("option", { value: "\uAD50\uD658", children: "\uAD50\uD658" }), _jsx("option", { value: "\uBC18\uD488", children: "\uBC18\uD488" }), _jsx("option", { value: "\uD658\uBD88", children: "\uD658\uBD88" }), _jsx("option", { value: "\uBD80\uC18D\uD488", children: "\uBD80\uC18D\uD488" }), _jsx("option", { value: "\uAE30\uD0C0", children: "\uAE30\uD0C0" })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "status", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC0C1\uD0DC" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "status", name: "status", value: newProcess.status || '', onChange: handleNewProcessChange, children: [_jsx("option", { value: "\uC811\uC218", children: "\uC811\uC218" }), _jsx("option", { value: "\uC9C4\uD589\uC911", children: "\uC9C4\uD589\uC911" }), _jsx("option", { value: "\uC644\uB8CC", children: "\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "send_courier_fee", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC18\uD488\uBC30\uC1A1\uBE44" }), _jsx("input", { type: "number", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "send_courier_fee", name: "send_courier_fee", value: newProcess.send_courier_fee || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "courier_fee", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC30\uC1A1\uBE44" }), _jsx("input", { type: "number", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "courier_fee", name: "courier_fee", value: newProcess.courier_fee || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "return_box", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC30\uC1A1 \uBC15\uC2A4\uC218\uB7C9" }), _jsx("input", { type: "number", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "return_box", name: "return_box", value: newProcess.return_box || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "send_box", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC18\uD488 \uBC15\uC2A4\uC218\uB7C9" }), _jsx("input", { type: "number", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "send_box", name: "send_box", value: newProcess.send_box || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "return_fee_type", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC30\uC1A1\uBE44 \uBD80\uB2F4" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "return_fee_type", name: "return_fee_type", value: newProcess.return_fee_type || '', onChange: handleNewProcessChange, children: [_jsx("option", { value: "\uACE0\uAC1D\uBD80\uB2F4", children: "\uACE0\uAC1D\uBD80\uB2F4" }), _jsx("option", { value: "\uC5C5\uCCB4\uBD80\uB2F4", children: "\uC5C5\uCCB4\uBD80\uB2F4" })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "send_fee_type", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC18\uD488\uBE44 \uBD80\uB2F4" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "send_fee_type", name: "send_fee_type", value: newProcess.send_fee_type || '', onChange: handleNewProcessChange, children: [_jsx("option", { value: "\uACE0\uAC1D\uBD80\uB2F4", children: "\uACE0\uAC1D\uBD80\uB2F4" }), _jsx("option", { value: "\uC5C5\uCCB4\uBD80\uB2F4", children: "\uC5C5\uCCB4\uBD80\uB2F4" })] })] }), _jsxs("div", { className: "col-span-2", children: [_jsx("label", { htmlFor: "courier_company", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC30\uC1A1 \uD0DD\uBC30\uC0AC" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "courier_company", name: "courier_company", value: newProcess.courier_company || '', onChange: handleNewProcessChange })] }), _jsxs("div", { className: "col-span-2", children: [_jsx("label", { htmlFor: "send_courier_company", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uBC18\uD488 \uD0DD\uBC30\uC0AC" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "send_courier_company", name: "send_courier_company", value: newProcess.send_courier_company || '', onChange: handleNewProcessChange })] }), _jsxs("div", { className: "col-span-4", children: [_jsx("label", { htmlFor: "claim_content", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC811\uC218\uB0B4\uC6A9" }), _jsx("textarea", { className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", id: "claim_content", name: "claim_content", rows: 3, value: newProcess.claim_content || '', onChange: handleNewProcessChange })] })] })] }), _jsxs("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: [_jsx("div", { className: "bg-gray-50 px-4 py-2 border-b border-gray-200 rounded-t-lg", children: _jsx("h6", { className: "font-bold text-sm text-gray-900", children: "\uD658\uBD88 \uC815\uBCF4" }) }), _jsxs("div", { className: "p-4 grid grid-cols-3 gap-3", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "bank_name", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC740\uD589\uBA85" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "bank_name", name: "bank_name", value: newProcess.bank_name || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "account_number", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uACC4\uC88C\uBC88\uD638" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "account_number", name: "account_number", value: newProcess.account_number || '', onChange: handleNewProcessChange })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "depositor", className: "block text-sm font-medium text-gray-700 mb-1", children: "\uC785\uAE08\uC790\uBA85" }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full", id: "depositor", name: "depositor", value: newProcess.depositor || '', onChange: handleNewProcessChange })] })] })] })] }) }), _jsxs("div", { className: "flex justify-end gap-2 px-6 py-4 border-t border-gray-200", children: [_jsx("button", { type: "button", className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: () => setShowProcessModal(false), children: "\uCDE8\uC18C" }), _jsx("button", { type: "button", className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: handleAddProcess, children: "\uB4F1\uB85D" })] })] }) })), _jsx(ImprovedClaimModal, { isOpen: showClaimModal, onClose: handleClaimClose, onSubmit: handleClaimSubmit, orderData: claimOrder, isLoading: isClaimLoading })] })] }));
};
export default CustomerConsultation;
